import {useRef, useEffect, useState, useMemo, useCallback} from 'react';
import {useHistory} from 'react-router-dom';
import FocusTrap from 'focus-trap-react';
import {useTranslation} from 'react-i18next';
import {cnb} from 'cnbuilder';
import mixpanel from 'mixpanel-browser';
import {useSelector, useDispatch} from 'react-redux';
import {appUpdate} from '@teladoc/fe-ccm/ui/app/app-actions';
import Link from '@teladoc/fe-ccm/ui/router/Link';
import some from 'lodash/some';
import Popover from '@teladoc/pulse/ui/Popover';
import Button from '@teladoc/pulse/ui/Button';
import IconProfileDefault from '@teladoc/pulse/icons/profile-default.svg';
import IconChatDefault from '@teladoc/pulse/icons/chat-default.svg';
import LanguageSwitcher from '@teladoc/fe-ccm/ui/common/language-switcher';
import KeyHandler from '@teladoc/fe-ccm/ui/common/utilities/key-handler';
import {useLoadedState} from '@teladoc/fe-ccm/ui/common/use-loaded-state';
import {TRACK_EVENTS_MAP} from '@teladoc/analytics';
import {useGateValue} from '@teladoc/statsig';
import AmplitudeClient from '@livongo/utilities/system/amplitude';
import {PRESCRIPTION_SAVINGS_TILE} from '@/feature-flags/config';
import IconMessageUnreadDefault from '@/icons/message-unread-default.svg';
import {getPrimaryRouteById, logoutRoute} from '@/router/routes';
import {
    BREAKPOINT_SIZE,
    NAV_MENU,
    FOOTER_CONTENT_STATE,
    EVENT_PROPS,
} from '@/config';
import SubNavigation from '@/Navigation/sub-navigation/SubNavigation';
import CommonUtils from '@/utils/common-utils';
import {useMessageCenterRoutes} from '@/utils/use-message-center-routes';
import PIWIKUtils from '@/utils/piwik-utils';
import {prefetchWebsdkShell} from '@/utils/prefetch-websdk';
import {prefetchRemoteEntry} from '@/utils/remote-module-loader';
import css from './navigation-menu.scss';

const NavigationMenu = () => {
    const menuBar = useRef(null);
    const accountBar = useRef(null);
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {push} = useHistory();
    const {breakpoint} = useSelector(state => state.app);
    const {hasUnread} = useSelector(state => state.chat);
    const {programs, userId} = useSelector(state => state.user);
    const {
        hasCareTeam,
        hasCardiometabolic,
        hasTelemed,
        oneAppActiveNav,
        hasMentalHealth,
        hasChatAvailable,
        hasMHGuideAvailable,
        hasConditionManagement,
        hasPartnerPrograms,
        hasNewCCMNavigation,
        hasGetCareNavigation,
        hasCommunicationPreferencesPulse,
        isGetCareButtonPrimary,
        branding,
        isActivityTrackerEnabled,
        hasCCMHealthInsights,
    } = useSelector(state => state.app);
    const hasPrescriptionSavings = useGateValue(PRESCRIPTION_SAVINGS_TILE);
    const isBrandingLoaded = useLoadedState(branding);
    const [updatedProgramRoutes, setUpdatedProgramRoutes] = useState([]);
    const [updatedAccountRoutes, setUpdatedAccountRoutes] = useState([]);
    const [healthInfoRoutes, setHealthInfoRoutes] = useState([]);
    const {getActiveRoutesAsString} = useMessageCenterRoutes();
    const mhAvailable = hasMentalHealth && hasMHGuideAvailable;
    const cmAvailable = hasConditionManagement && hasChatAvailable;
    const isMessageAvailable = mhAvailable || cmAvailable || hasCareTeam;
    const [activeMenu, setActiveMenu] = useState(null);
    const isRestrictedMember = useMemo(() => {
        return branding?.restricted_member_correctional_facilities;
    }, [branding]);

    useEffect(() => {
        setUpdatedAccountRoutes(
            CommonUtils.updateAccountRoutes({
                hasCommunicationPreferencesPulse,
            })
        );
    }, [hasCommunicationPreferencesPulse]);

    useEffect(() => {
        if (!isBrandingLoaded) {
            setHealthInfoRoutes([]);

            return;
        }
        setHealthInfoRoutes(
            CommonUtils.updateHealthInfoMenuRoute({
                hasTelemed,
                branding,
                isActivityTrackerEnabled,
                hasCardiometabolic,
                hasCCMHealthInsights,
                hasPrescriptionSavings,
            })
        );
    }, [
        isBrandingLoaded,
        hasTelemed,
        branding,
        isActivityTrackerEnabled,
        hasCardiometabolic,
        hasCCMHealthInsights,
        hasPrescriptionSavings,
    ]);

    const resetShowSidebar = (id = NAV_MENU.Home.id) => {
        dispatch(appUpdate({oneAppActiveNav: id}));
        dispatch(
            appUpdate({
                oneAppActiveProgram: FOOTER_CONTENT_STATE.TeleMed.id,
            })
        );
    };

    const onDashboardClick = () => {
        resetShowSidebar();
        mixpanel.track('navigation.button.clicked', {
            level: 1,
            button: 'Home',
        });

        AmplitudeClient.track(TRACK_EVENTS_MAP.navigationButtonClicked, {
            level: 1,
            category: 'navigation',
            button: EVENT_PROPS.Home,
            // eslint-disable-next-line camelcase
            product_line: EVENT_PROPS.Global,
        });
    };

    const onProgramsToggle = isShowing => {
        if (isShowing) {
            prefetchWebsdkShell();
            mixpanel.track('navigation.button.clicked', {
                level: 1,
                button: 'Programs',
            });

            PIWIKUtils.buildTrackingEvent({
                eventAction: 'Header Navigation Clicked',
                eventName: 'Programs Menu',
                userId,
            });

            AmplitudeClient.track(TRACK_EVENTS_MAP.navigationButtonClicked, {
                level: 1,
                category: 'navigation',
                button: EVENT_PROPS.Programs,
                // eslint-disable-next-line camelcase
                product_line: EVENT_PROPS.Global,
            });
        }
    };

    const onHealthInfoToggle = isShowing => {
        if (isShowing) {
            if (hasTelemed) {
                prefetchWebsdkShell();
            }

            if (hasPrescriptionSavings) {
                prefetchRemoteEntry('feDspApp');
            }

            mixpanel.track('navigation.button.clicked', {
                level: 1,
                button: 'Health Info',
            });

            AmplitudeClient.track(TRACK_EVENTS_MAP.navigationButtonClicked, {
                level: 1,
                category: 'navigation',
                button: EVENT_PROPS.HealthInfo,
                // eslint-disable-next-line camelcase
                product_line: EVENT_PROPS.Global,
            });
        }
    };

    const onAccountToggle = isShowing => {
        if (isShowing) {
            mixpanel.track('navigation.button.clicked', {
                level: 1,
                button: 'Account',
            });

            AmplitudeClient.track(TRACK_EVENTS_MAP.navigationButtonClicked, {
                level: 1,
                category: 'navigation',
                button: EVENT_PROPS.Account,
                // eslint-disable-next-line camelcase
                product_line: EVENT_PROPS.Global,
            });
        }
    };

    const updateSidebarState = id => {
        dispatch(appUpdate({oneAppActiveNav: NAV_MENU.Programs.id}));

        if (id === 'conditionManagement') {
            CommonUtils.updateSidebar(true, dispatch);
            dispatch(
                appUpdate({
                    oneAppActiveProgram:
                        FOOTER_CONTENT_STATE.ConditionManagement.id,
                })
            );
            setTimeout(() => {
                const programsButton = document.querySelector(
                    '[name="oneapp.conditionManagementHome"], [name="oneapp.programs"]'
                );

                if (programsButton) {
                    programsButton.blur();
                }

                const el = document.querySelector('.Page-mainHeading');

                el.focus();
            }, 100);
        } else if (id === 'mentalHealth') {
            dispatch(
                appUpdate({
                    oneAppActiveProgram: FOOTER_CONTENT_STATE.MyStrength.id,
                })
            );
        } else if (id === 'partnerPrograms') {
            dispatch(
                appUpdate({
                    oneAppActiveProgram:
                        FOOTER_CONTENT_STATE.PartnerPrograms.id,
                })
            );
        } else {
            CommonUtils.updateSidebar(false, dispatch);
            dispatch(
                appUpdate({
                    oneAppActiveProgram: FOOTER_CONTENT_STATE.TeleMed.id,
                })
            );
        }
    };

    const onCCMHomeOpen = () => {
        dispatch(
            appUpdate({
                oneAppActiveNav: NAV_MENU.ConditionManagementHome.id,
            })
        );
        CommonUtils.updateSidebar(true, dispatch);
        dispatch(
            appUpdate({
                oneAppActiveProgram:
                    FOOTER_CONTENT_STATE.ConditionManagement.id,
            })
        );
        mixpanel.track('navigation.button.clicked', {
            level: 2,
            button: 'Programs',
        });

        AmplitudeClient.track(TRACK_EVENTS_MAP.navigationButtonClicked, {
            level: 1,
            category: 'navigation',
            // eslint-disable-next-line camelcase
            product_line: EVENT_PROPS.Global,
            button: EVENT_PROPS.ConditionManagement,
        });
    };

    const onProgramsMenuClick = id => {
        PIWIKUtils.buildTrackingEvent({
            eventAction: 'Header Navigation Clicked',
            eventName: id,
            userId,
        });

        updateSidebarState(id);
    };

    const onMessagesOpen = () => {
        CommonUtils.updateSidebar(false, dispatch);
        dispatch(appUpdate({oneAppActiveNav: NAV_MENU.Message.id}));
        mixpanel.track('navigation.button.clicked', {
            level: 2,
            button: 'Messages',
        });

        AmplitudeClient.track(TRACK_EVENTS_MAP.navigationButtonClicked, {
            level: 1,
            category: 'navigation',
            button: EVENT_PROPS.Messages,
            // eslint-disable-next-line camelcase
            product_line: EVENT_PROPS.Global,
            [EVENT_PROPS.Inbox]: getActiveRoutesAsString(),
        });
    };

    const onGetCareClick = () => {
        const careOptionsRoute = getPrimaryRouteById(NAV_MENU.CareOptions.id);
        const {amplitudeProperty, path} = careOptionsRoute;

        push(path);

        /* eslint-disable camelcase */
        AmplitudeClient.track(TRACK_EVENTS_MAP.getCareButtonClicked, {
            category: amplitudeProperty.category,
            cta: amplitudeProperty.button,
            product_line: amplitudeProperty.productLine,
            level: amplitudeProperty.level,
        });
        /* eslint-enable camelcase */
    };

    const setCurrentState = evt => {
        if (!evt.activeState) {
            const pathname = document?.location?.pathname;

            if (pathname?.includes('message-center')) {
                dispatch(
                    appUpdate({oneAppActiveNav: NAV_MENU.MessageCenter.id})
                );
            } else if (
                pathname?.includes('condition-management') ||
                pathname?.includes('mental-health') ||
                pathname?.includes('/partner_programs')
            ) {
                dispatch(appUpdate({oneAppActiveNav: NAV_MENU.Programs.id}));
            } else if (
                pathname?.includes('/account') ||
                pathname?.includes('/help')
            ) {
                dispatch(appUpdate({oneAppActiveNav: NAV_MENU.Account.id}));
            } else if (
                pathname?.includes('/health-info') ||
                pathname?.includes('/consultation_history') ||
                pathname?.includes('/health-insights') ||
                pathname?.includes('/health-insights-report')
            ) {
                dispatch(appUpdate({oneAppActiveNav: NAV_MENU.HealthInfo.id}));
            } else if (pathname?.includes('message')) {
                dispatch(appUpdate({oneAppActiveNav: NAV_MENU.Message.id}));
            } else if (pathname?.includes('/sitemap')) {
                dispatch(appUpdate({oneAppActiveNav: NAV_MENU.Sitemap.id}));
            } else if (pathname?.includes('/care-options')) {
                dispatch(appUpdate({oneAppActiveNav: NAV_MENU.CareOptions.id}));
            } else {
                dispatch(appUpdate({oneAppActiveNav: NAV_MENU.Home.id}));
            }
            evt.activeState = true;
        }
    };

    // event to Handle Browser Back & Forward button
    window.addEventListener('popstate', setCurrentState);

    useEffect(() => {
        setUpdatedProgramRoutes(
            CommonUtils.updateProgramsMenuRoutes({
                programs,
                appState: {
                    hasConditionManagement,
                    hasMentalHealth,
                    hasPartnerPrograms,
                },
            })
        );
    }, [programs, hasConditionManagement, hasMentalHealth, hasPartnerPrograms]);

    const handleMenuClick = useCallback(index => {
        setActiveMenu(prevMenu => (prevMenu === index ? null : index));
    }, []);

    const handleClickOutside = useCallback(event => {
        if (
            menuBar.current &&
            !menuBar.current.contains(event.target) &&
            accountBar.current &&
            !accountBar.current.contains(event.target)
        ) {
            setActiveMenu(null);
        }
    }, []);

    useEffect(() => {
        document.addEventListener('click', handleClickOutside);

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [handleClickOutside]);

    return (
        <nav
            data-testid="header-nav-menu"
            className={cnb(css.navContainer, {
                [css.navContainerWithGetCare]: hasGetCareNavigation,
            })}
        >
            <div className={css.menuItemsLeft}>
                <ul ref={menuBar} className={css.navItemsList} role="menubar">
                    <li
                        role="none"
                        onClick={() => handleMenuClick(NAV_MENU.Home.id)}
                    >
                        <Link
                            to={getPrimaryRouteById(NAV_MENU.Home.id).path}
                            className={cnb(css.home, {
                                [css.navActiveMenu]:
                                    oneAppActiveNav === NAV_MENU.Home.id,
                            })}
                            name="oneapp.home"
                            activeClassName={css.navActiveMenu}
                            aria-label={t('oneapp.home')}
                            tabIndex={0}
                            onClick={onDashboardClick}
                            role="menuitem"
                            data-testid="Global-home"
                            onKeyDown={e =>
                                KeyHandler.keyDown(e, menuBar?.current)
                            }
                        >
                            {t('oneapp.home')}
                        </Link>
                    </li>
                    {hasNewCCMNavigation && hasConditionManagement ? (
                        <li role="none">
                            <Link
                                to={
                                    getPrimaryRouteById(
                                        NAV_MENU.ConditionManagementHome.id
                                    ).path
                                }
                                className={cnb(css.home, {
                                    [css.navActiveMenu]:
                                        oneAppActiveNav ===
                                        NAV_MENU.ConditionManagementHome.id,
                                })}
                                name="oneapp.conditionManagementHome"
                                activeClassName={css.navActiveMenu}
                                aria-label={t('oneapp.conditionManagementHome')}
                                onClick={onCCMHomeOpen}
                                role="menuitem"
                                onKeyDown={e =>
                                    KeyHandler.keyDown(e, menuBar?.current)
                                }
                            >
                                {t('oneapp.conditionManagementHome')}
                            </Link>
                        </li>
                    ) : !hasNewCCMNavigation &&
                      CommonUtils.showProgramsMenu({
                          hasConditionManagement,
                          hasPartnerPrograms,
                          hasMentalHealth,
                      }) ? (
                        <li
                            className={cnb(css.menuItemList, {
                                [css.activeMenuIndex]:
                                    activeMenu === NAV_MENU.Programs.id,
                            })}
                            role="none"
                            onClick={() =>
                                handleMenuClick(NAV_MENU.Programs.id)
                            }
                        >
                            <Popover
                                options={{overflowContainer: true}}
                                hideOnContentClick
                                hideOnOutsideClick
                                onToggle={onProgramsToggle}
                                trigger={
                                    <Button
                                        className={cnb(css.subNavMenu, {
                                            [css.navActiveMenu]:
                                                oneAppActiveNav ===
                                                NAV_MENU.Programs.id,
                                        })}
                                        name="oneapp.programs"
                                        aria-label={t('oneapp.programs')}
                                        testAttr="Global-programs"
                                        role="menuitem"
                                        onKeyDown={e =>
                                            KeyHandler.keyDown(
                                                e,
                                                menuBar?.current
                                            )
                                        }
                                    >
                                        <span className={css.navItemText}>
                                            {t('oneapp.programs')}
                                        </span>
                                    </Button>
                                }
                                content={
                                    <FocusTrap
                                        focusTrapOptions={{
                                            clickOutsideDeactivates: true,
                                        }}
                                    >
                                        <div>
                                            <SubNavigation
                                                className={
                                                    css.memberDropdownLeft
                                                }
                                                itemClassName={css.dropdownItem}
                                                activeItemClassName={
                                                    css.dropdownActiveItem
                                                }
                                                tabName={NAV_MENU.Programs.id}
                                                activeRoute={
                                                    updatedProgramRoutes
                                                }
                                                onItemClick={
                                                    onProgramsMenuClick
                                                }
                                            />
                                        </div>
                                    </FocusTrap>
                                }
                            />
                        </li>
                    ) : null}
                    {Object.keys(branding).length > 1 &&
                        !branding?.supress_health_info && (
                            <li
                                className={cnb(css.menuItemList, {
                                    [css.activeMenuIndex]:
                                        activeMenu === NAV_MENU.HealthInfo.id,
                                })}
                                role="none"
                                onClick={() =>
                                    handleMenuClick(NAV_MENU.HealthInfo.id)
                                }
                            >
                                <Popover
                                    options={{overflowContainer: true}}
                                    hideOnContentClick
                                    hideOnOutsideClick
                                    onToggle={onHealthInfoToggle}
                                    trigger={
                                        <Button
                                            className={cnb(css.subNavMenu, {
                                                [css.navActiveMenu]:
                                                    oneAppActiveNav ===
                                                    NAV_MENU.HealthInfo.id,
                                            })}
                                            role="menuitem"
                                            name="oneapp.healthInfo"
                                            aria-label={t('oneapp.healthInfo')}
                                            testAttr="Global-healthInfo"
                                            onKeyDown={e =>
                                                KeyHandler.keyDown(
                                                    e,
                                                    menuBar?.current
                                                )
                                            }
                                        >
                                            <span className={css.navItemText}>
                                                {t('oneapp.healthInfo')}
                                            </span>
                                        </Button>
                                    }
                                    content={
                                        <FocusTrap
                                            focusTrapOptions={{
                                                clickOutsideDeactivates: true,
                                            }}
                                        >
                                            <div>
                                                <SubNavigation
                                                    className={`${css.memberDropdownLeftHealthInfo} ${css.menuLeftDropdownHeight}`}
                                                    itemClassName={
                                                        css.dropdownItem
                                                    }
                                                    activeItemClassName={
                                                        css.dropdownActiveItem
                                                    }
                                                    tabName={
                                                        NAV_MENU.HealthInfo.id
                                                    }
                                                    activeRoute={
                                                        healthInfoRoutes
                                                    }
                                                    onItemClick={() =>
                                                        resetShowSidebar(
                                                            NAV_MENU.HealthInfo
                                                                .id
                                                        )
                                                    }
                                                />
                                            </div>
                                        </FocusTrap>
                                    }
                                />
                            </li>
                        )}
                </ul>
            </div>
            <div className={css.menuItemsRight}>
                <ul
                    ref={accountBar}
                    className={css.navItemsList}
                    role="menubar"
                >
                    {isMessageAvailable && (
                        <li
                            className={cnb(css.menuItemList)}
                            role="none"
                            onClick={() =>
                                handleMenuClick(NAV_MENU.MessageCenter.id)
                            }
                        >
                            <Link
                                className={cnb(
                                    css.subNavMenu,
                                    css.menuTextColor,
                                    {
                                        [css.navActiveMenu]:
                                            oneAppActiveNav ===
                                            NAV_MENU.MessageCenter.id,
                                    }
                                )}
                                activeClassName={css.navActiveMenu}
                                role="menuitem"
                                name="oneapp.messages"
                                aria-label={t(
                                    some(hasUnread)
                                        ? 'oneapp.unreadMessages'
                                        : 'oneapp.messages'
                                )}
                                onClick={onMessagesOpen}
                                data-testid="Global-messages"
                                to={
                                    getPrimaryRouteById(
                                        NAV_MENU.MessageCenter.id
                                    ).path
                                }
                                onKeyDown={e =>
                                    KeyHandler.keyDown(e, accountBar?.current)
                                }
                            >
                                <span className={css.navItemText}>
                                    {some(hasUnread) ? (
                                        <IconMessageUnreadDefault
                                            className={css.navIconLarge}
                                        />
                                    ) : (
                                        <IconChatDefault
                                            className={css.navIconLarge}
                                        />
                                    )}
                                    <span className={css.navText}>
                                        {t('oneapp.messages')}
                                    </span>
                                </span>
                            </Link>
                        </li>
                    )}
                    {isBrandingLoaded && !isRestrictedMember && (
                        <li
                            className={cnb(css.menuItemList, {
                                [css.activeMenuIndex]:
                                    activeMenu === NAV_MENU.Account.id,
                            })}
                            role="none"
                            onClick={() => handleMenuClick(NAV_MENU.Account.id)}
                        >
                            <Popover
                                options={{overflowContainer: true}}
                                hideOnContentClick
                                hideOnOutsideClick
                                onToggle={onAccountToggle}
                                trigger={
                                    <Button
                                        className={cnb(
                                            css.subNavMenu,
                                            css.menuTextColor,
                                            {
                                                [css.navActiveMenu]:
                                                    oneAppActiveNav ===
                                                    NAV_MENU.Account.id,
                                            }
                                        )}
                                        role="menuitem"
                                        name="oneapp.account"
                                        aria-label={t('oneapp.account')}
                                        testAttr="Global-account"
                                        onKeyDown={e =>
                                            KeyHandler.keyDown(
                                                e,
                                                accountBar?.current
                                            )
                                        }
                                    >
                                        <span className={css.navItemText}>
                                            <IconProfileDefault
                                                className={css.navIconLarge}
                                            />
                                            <span className={css.navText}>
                                                {t('oneapp.account')}
                                            </span>
                                        </span>
                                    </Button>
                                }
                                content={
                                    <FocusTrap
                                        focusTrapOptions={{
                                            clickOutsideDeactivates: true,
                                        }}
                                    >
                                        <div>
                                            <SubNavigation
                                                className={`${css.memberDropdown} ${css.menuRightDropdownHeight}`}
                                                itemClassName={css.dropdownItem}
                                                activeItemClassName={
                                                    css.dropdownActiveItem
                                                }
                                                onItemClick={() =>
                                                    resetShowSidebar(
                                                        NAV_MENU.Account.id
                                                    )
                                                }
                                                tabName={NAV_MENU.Account.id}
                                                activeRoute={[
                                                    ...CommonUtils.filterRoutesByBrandSuppressedRoutes(
                                                        updatedAccountRoutes,
                                                        branding
                                                    ),
                                                    ...logoutRoute,
                                                ]}
                                            />
                                        </div>
                                    </FocusTrap>
                                }
                            />
                        </li>
                    )}
                    {breakpoint !== BREAKPOINT_SIZE.Small && (
                        <li
                            role="none"
                            className={cnb(css.menuItemList, {
                                [css.activeMenuIndex]:
                                    activeMenu === NAV_MENU.LanguageSwitcher.id,
                            })}
                            onClick={() =>
                                handleMenuClick(NAV_MENU.LanguageSwitcher.id)
                            }
                        >
                            <LanguageSwitcher
                                keyDownPress={e =>
                                    KeyHandler.keyDown(e, accountBar?.current)
                                }
                            />
                        </li>
                    )}
                    {hasGetCareNavigation && (
                        <li role="none">
                            <Button
                                className={css.getCareButton}
                                variant={
                                    isGetCareButtonPrimary
                                        ? 'primary'
                                        : 'secondary'
                                }
                                size="small"
                                block="true"
                                onClick={onGetCareClick}
                                role="menuitem"
                            >
                                <span>{t('oneapp.getCare')}</span>
                            </Button>
                        </li>
                    )}
                </ul>
            </div>
        </nav>
    );
};

export default NavigationMenu;
